import { useStore } from 'vuex'
import { CoreEntityDto } from '@/interfaces/core.interface'
import { useGeneralStore } from '@/pinia'
import { FeatureCollection, Geometry } from '@turf/turf'
import { PoiInterface as navVisPoi } from '@navvis/ivion'
import { PoiProperties } from '@/interfaces/poi.interface'
import { Room, ROOM_PROPERTIES } from '@/interfaces/room.interface'
import { useI18n } from 'vue-i18n'

type LevelRooms = {
  data: FeatureCollection<Geometry, ROOM_PROPERTIES>
}
export type subHeaderItem = {
  isBadge?: boolean
  text: string
}

function findRoomForPoi(
  roomsForSelectedLevel: LevelRooms,
  selectedPoi: PoiProperties
): Room | undefined {
  return roomsForSelectedLevel.data.features.find((room) => room.id === selectedPoi.coreId) as
    | Room
    | undefined
}

export function useEntitySubHeaders() {
  const { t } = useI18n()
  const store = useStore()
  const piniaStore = useGeneralStore()

  function generateConstructionSubHeaders(entity: CoreEntityDto | undefined): subHeaderItem[] {
    const items: subHeaderItem[] = []
    if (!entity) return items
    if (entity.nameTenant) {
      items.push({
        text: `${t('general__text__tenant-name')}: ${entity.nameTenant}`
      })
    }
    if (entity.label) {
      items.push({
        text: `${t('general__data__marking-label')}: ${entity.label}`
      })
    }
    if (entity.labelTenant) {
      items.push({
        text: `${t('general__text__tenant-label')}: ${entity.labelTenant}`
      })
    }
    return items
  }

  function generateLevelSubHeaders(entity: CoreEntityDto | undefined): subHeaderItem[] {
    const items: subHeaderItem[] = []
    if (!entity) return items
    if (store.state.mapview.selectedBuilding) {
      items.push({
        isBadge: true,
        text: store.state.mapview.selectedBuilding.name
      })
    }
    if (entity.nameTenant) {
      items.push({
        text: `${t('general__text__tenant-name')}: ${entity.nameTenant}`
      })
    }
    if (entity.label) {
      items.push({
        text: `${t('general__data__marking-label')}: ${entity.label}`
      })
    }
    if (entity.labelTenant) {
      items.push({
        text: `${t('general__text__tenant-label')}: ${entity.labelTenant}`
      })
    }
    return items
  }

  function generateLineSubHeaders(): subHeaderItem[] {
    const items: subHeaderItem[] = []
    if (!piniaStore.selectedLine) return items
    if (store.getters['mapview/selectedRealEstateName']) {
      items.push({
        isBadge: true,
        text: store.getters['mapview/selectedRealEstateName']
      })
    }
    return items
  }
  function generateZoneSubHeaders(): subHeaderItem[] {
    const items: subHeaderItem[] = []
    if (!piniaStore.selectedZone) return items
    if (store.getters.selectedLevelNumber) {
      items.push({
        isBadge: true,
        text: `${t('general__text__level')} ${store.getters.selectedLevelNumber}`
      })
    } else if (!store.state.selectedLevel && store.getters['mapview/selectedRealEstateName']) {
      items.push({
        isBadge: true,
        text: store.getters['mapview/selectedRealEstateName']
      })
    }
    return items
  }

  function generatePoiSubHeaders(): subHeaderItem[] {
    const items: subHeaderItem[] = []
    if (!store.state.selectedPoi) return items
    function addLevelOrRealEstateBadge() {
      if (store.getters.selectedLevelNumber) {
        items.push({
          isBadge: true,
          text: `${t('general__text__level')} ${store.getters.selectedLevelNumber}`
        })
      } else if (!store.state.selectedLevel && store.getters['mapview/selectedRealEstateName']) {
        items.push({
          isBadge: true,
          text: store.getters['mapview/selectedRealEstateName']
        })
      }
    }
    const isBuildingSelected = store.state.mapview?.selectedBuilding
      ? Object.keys(store.state.mapview.selectedBuilding).length > 0
      : undefined
    const isPoiFromBackend =
      !(store.state.selectedPoi as unknown as navVisPoi)?.title &&
      !(store.state.selectedPoi as unknown as navVisPoi)?.pov
    // If the POI is from backend, we need to check if it is connected to a room
    if (isPoiFromBackend && isBuildingSelected) {
      const roomWithPoi = findRoomForPoi(store.state.roomsForSelectedLevel, store.state.selectedPoi)
      if (roomWithPoi) {
        items.push({
          isBadge: true,
          text: roomWithPoi.properties.displayName
        })
      } else {
        addLevelOrRealEstateBadge()
      }
      return items
    }
    // If the POI has no room or level connection, check its real estate instead
    addLevelOrRealEstateBadge()
    return items
  }

  function generateSpaceSubHeaders(entity: CoreEntityDto | undefined): subHeaderItem[] {
    const items: subHeaderItem[] = []
    if (!entity) return items
    if (store.getters.selectedLevelNumber) {
      items.push({
        isBadge: true,
        text: `${t('general__text__level')} ${store.getters.selectedLevelNumber}`
      })
    }

    let subHeader = ''
    if (entity.nameTenant && entity.labelTenant) {
      subHeader = `${t('general__text__tenant-name')}: ${entity.labelTenant} ${entity.nameTenant}`
    } else if (entity.nameTenant) {
      subHeader = `${t('general__text__tenant-name')}: ${entity.nameTenant}`
    } else if (entity.labelTenant) {
      subHeader = `${t('general__text__tenant-label')}: ${entity.labelTenant}`
    }

    if (subHeader) {
      items.push({
        text: subHeader
      })
    }
    return items
  }

  return {
    generateConstructionSubHeaders,
    generateLevelSubHeaders,
    generateLineSubHeaders,
    generateZoneSubHeaders,
    generatePoiSubHeaders,
    generateSpaceSubHeaders
  }
}
